/**
 * `<page-publish>` — the draft review screen and the publish confirmation, routed
 * at `/instances/:id/publish`.
 *
 * **This is the most consequential screen in the product**, and everything about
 * it is arranged around one obligation: the blast radius is stated, in the
 * server's own words, *before* the confirm button — and the two change classes
 * never look alike.
 *
 * - **Reload class** (`SC-F605`): no restart, no outage. In-flight queries finish
 *   on the policy set they started with; the next query sees the new one. A
 *   neutral dialog, a primary button, no acknowledgement of an interruption
 *   because there is none.
 * - **Restart class** (`SC-F606`): the task is replaced. Roughly 10 to 25 seconds
 *   during which queries fail, and DuckDB re-warms afterwards. A destructive
 *   dialog, a danger button, and an explicit acknowledgement that is not the same
 *   click as saving a draft.
 *
 * The consequence block renders `blast_radius` and `query_loss_warning`
 * **verbatim** from the classifier (`SC-F607`). They are not paraphrased and not
 * softened: `02` §5.3 is explicit that copy which disagrees with the refusal — or
 * which promises an outage that does not happen — trains users to ignore both. And
 * the screen is honest about the drain gap, because zero query loss on a
 * restart-class publish is **not achievable today**: Sluice's REST drain is a
 * hard-coded 10 s and `ShutdownTimeout` is not configurable.
 *
 * Also implements `SC-F604` (validate before publish; the gate is server-side and
 * is not a UI affordance), `SC-F608` (the digest read-back shown as the distinct
 * step it is, and `publish_failed` shown with the previous generation still live),
 * `SC-F611` (the co-author roll-up, and the acknowledgement the API demands before
 * shipping somebody else's work), `SC-F709` (the gate ladder, with `not-run`
 * rendered as itself and never as a green tick) and `SC-F409` (a datasource change
 * is always restart-class, and the dialog says which changes cost the outage).
 *
 * Stories: `SC-S604`, `SC-S605`, `SC-S606`, `SC-S607`, `SC-S608`, `SC-S610`,
 * `SC-S713`.
 */

import { LitElement, css, html, nothing, type PropertyValues, type TemplateResult } from "lit";
import { customElement, property, state } from "lit/decorators.js";

import "../components/index.js";
import {
  publishClassVariant,
  toastPersistent,
  type BnBadgeVariant,
  type BnColumn,
  type BnKvRow,
  type BnRow,
} from "../components/index.js";

import { ApiError, errorDetail, parseError } from "../api/errors.js";
import {
  getDraft,
  listGenerations,
  CHANGE_CLASS_LABELS,
  GATE_STEP_LABELS,
  type Draft,
  type DraftAuthor,
  type GateFinding,
  type GateStep,
  type Generation,
} from "../api/generations.js";
import { getInstance, type Instance } from "../api/instances.js";
import { isTerminal, jobFailure, watchJob, type Job } from "../api/jobs.js";
import { discardDraft, publish, type PublishResult } from "../api/publish.js";
import { currentUser } from "../api/auth.js";
import { hrefFor, navigate } from "../nav.js";

/** The gate ladder's tones. `not-run` is neutral and never green: `06` §3.9's own
 * example answers `not-run` for the proxied rungs on this route precisely so a UI
 * cannot render an unrun step as a pass. */
const STEP_TONE: Record<string, BnBadgeVariant> = {
  pass: "success",
  fail: "error",
  warn: "warning",
  "not-run": "neutral",
  skipped: "neutral",
};

const STEP_WORD: Record<string, string> = {
  pass: "passed",
  fail: "failed",
  warn: "warning",
  "not-run": "not run",
  skipped: "not applicable",
};

const FINDING_TONE: Record<string, BnBadgeVariant> = {
  error: "error",
  "blocking-warning": "warning",
  warning: "warning",
  info: "info",
  hint: "neutral",
};

const CHANGE_COLUMNS: BnColumn[] = [
  { key: "op", label: "", width: "1px" },
  { key: "object", label: "Object" },
  { key: "who", label: "Last changed by" },
];

@customElement("page-publish")
export class PagePublish extends LitElement {
  /** The instance record id, from the route. */
  @property() instanceId = "";

  @state() private draft: Draft | null = null;
  @state() private instance: Instance | null = null;
  /** The live and last-failed generations, for the two statements this screen has
   * to be able to make: what is running, and what failed last. */
  @state() private live: Generation | null = null;
  @state() private lastFailed: Generation | null = null;

  @state() private loading = true;
  @state() private failure: ApiError | null = null;

  /* ── the publish ceremony ─────────────────────────────────────────── */
  @state() private confirming = false;
  @state() private message = "";
  @state() private acceptRestart = false;
  @state() private acknowledgeAuthors = false;
  @state() private overrideReason = "";
  @state() private confirmName = "";
  @state() private lockoutBlocked = false;
  @state() private publishing = false;
  @state() private publishFailure: ApiError | null = null;

  @state() private job: Job<PublishResult> | null = null;
  @state() private result: PublishResult | null = null;
  /** True when the job watch broke rather than the job — a distinction the copy has
   * to keep, because the publish is still running either way. */
  @state() private watchLost = false;

  /* ── the discard ceremony ─────────────────────────────────────────── */
  @state() private discarding = false;
  @state() private discardOthers = false;
  @state() private discardBusy = false;
  @state() private discardFailure: ApiError | null = null;

  private watchAbort?: AbortController;

  /** The signed-in account id, so "not yours" is decided the same way the API
   * decides it: by who last touched the object. */
  private readonly accountId = currentUser()?.id ?? "";

  static override styles = css`
    :host {
      display: block;
      font-family: var(--bino-font-sans);
      color: var(--bino-text);
    }
    .back {
      display: inline-block;
      margin-bottom: var(--bino-space-md);
      font-size: var(--bino-font-size-md);
      color: var(--bino-text-secondary);
      text-decoration: none;
      border-radius: var(--bino-radius-sm);
    }
    .back:hover {
      color: var(--bino-text);
    }
    .back:focus-visible {
      outline: none;
      box-shadow: var(--bino-focus-ring-shadow);
    }
    header {
      display: flex;
      align-items: flex-start;
      flex-wrap: wrap;
      gap: var(--bino-space-md);
      margin-bottom: var(--bino-space-lg);
    }
    .head-text {
      flex: 1 1 26rem;
      min-width: 0;
    }
    h1 {
      margin: 0;
      font-size: var(--bino-font-size-xl);
      font-weight: 600;
    }
    h2 {
      margin: 0 0 var(--bino-space-sm);
      font-size: var(--bino-font-size-md);
      font-weight: 600;
    }
    .lede {
      margin: var(--bino-space-xs) 0 0;
      font-size: var(--bino-font-size-md);
      color: var(--bino-text-secondary);
      max-width: 74ch;
    }
    .head-actions {
      display: flex;
      gap: var(--bino-space-sm);
      flex-wrap: wrap;
      align-items: center;
    }
    .stack {
      display: grid;
      gap: var(--bino-space-lg);
    }
    p.copy {
      margin: 0 0 var(--bino-space-sm);
      max-width: 74ch;
      font-size: var(--bino-font-size-md);
      color: var(--bino-text-secondary);
    }
    p.copy:last-child {
      margin-bottom: 0;
    }
    p.copy strong {
      color: var(--bino-text);
    }
    code {
      font-family: var(--bino-font-mono);
      font-size: var(--bino-font-size-base);
    }
    .meta {
      font-size: var(--bino-font-size-sm);
      color: var(--bino-text-secondary);
    }
    .sub {
      display: block;
      font-size: var(--bino-font-size-sm);
      color: var(--bino-text-secondary);
    }
    .op {
      font-family: var(--bino-font-mono);
      font-weight: 600;
    }
    .op.create {
      color: var(--bino-diff-add-text);
    }
    .op.delete {
      color: var(--bino-diff-del-text);
    }
    /* The blast-radius block on the page itself. It is the same sentence the dialog
       shows, deliberately: the screen and the confirmation must not be able to
       disagree about what a publish costs. */
    .radius {
      padding: var(--bino-space-md);
      border-radius: var(--bino-radius);
      border: 1px solid var(--bino-warning-border);
      background: var(--bino-warning-bg);
      color: var(--bino-warning-text);
    }
    .radius.restart {
      border-color: var(--bino-error-border);
      background: var(--bino-error-bg);
      color: var(--bino-text);
    }
    .radius.reload {
      border-color: var(--bino-border);
      background: var(--bino-surface-subtle);
      color: var(--bino-text);
    }
    .radius p {
      margin: 0 0 var(--bino-space-sm);
      max-width: 74ch;
      font-size: var(--bino-font-size-md);
    }
    .radius p:last-child {
      margin-bottom: 0;
    }
    .radius .head {
      display: flex;
      align-items: center;
      gap: var(--bino-space-sm);
      margin-bottom: var(--bino-space-sm);
      font-weight: 600;
    }
    ul.plain {
      margin: 0;
      padding-left: 1.1rem;
      font-size: var(--bino-font-size-md);
      color: var(--bino-text-secondary);
    }
    ul.plain li + li {
      margin-top: 0.25rem;
    }
    ul.gates {
      margin: 0;
      padding: 0;
      list-style: none;
    }
    ul.gates li {
      display: flex;
      align-items: baseline;
      gap: var(--bino-space-sm);
      padding: var(--bino-space-xs) 0;
      border-bottom: 1px solid var(--bino-border);
      font-size: var(--bino-font-size-md);
    }
    ul.gates li:last-child {
      border-bottom: none;
    }
    ul.gates .detail {
      flex: 1 1 24rem;
      min-width: 0;
      font-size: var(--bino-font-size-sm);
      color: var(--bino-text-secondary);
    }
    .findings {
      margin: 0;
      padding: 0;
      list-style: none;
    }
    .findings li {
      padding: var(--bino-space-sm) 0;
      border-bottom: 1px solid var(--bino-border);
    }
    .findings li:last-child {
      border-bottom: none;
    }
    .findings .where {
      font-family: var(--bino-font-mono);
      font-size: var(--bino-font-size-sm);
      color: var(--bino-text-secondary);
    }
    .findings .msg {
      margin: 0.25rem 0 0;
      max-width: 74ch;
      font-size: var(--bino-font-size-md);
      color: var(--bino-text);
    }
    .authors {
      margin: 0;
      padding: 0;
      list-style: none;
    }
    .authors li {
      display: flex;
      align-items: baseline;
      gap: var(--bino-space-sm);
      padding: var(--bino-space-xs) 0;
      font-size: var(--bino-font-size-md);
    }
    .steps {
      margin: 0;
      padding: 0;
      list-style: none;
      font-size: var(--bino-font-size-md);
    }
    .steps li {
      display: flex;
      align-items: baseline;
      gap: var(--bino-space-sm);
      padding: var(--bino-space-xs) 0;
    }
    .failure {
      display: flex;
      flex-direction: column;
      gap: var(--bino-space-md);
    }
    .failure p {
      margin: 0;
      max-width: 72ch;
      font-size: var(--bino-font-size-md);
      color: var(--bino-text-secondary);
    }
    .failure .actions {
      display: flex;
      gap: var(--bino-space-sm);
      flex-wrap: wrap;
    }
  `;

  override disconnectedCallback() {
    super.disconnectedCallback();
    this.watchAbort?.abort();
  }

  /** Driven from `willUpdate` alone: the router sets `instanceId` before the first
   * update, so loading from `connectedCallback` too would read the draft twice — and
   * the draft read runs the gate. */
  override willUpdate(changed: PropertyValues<this>) {
    if (changed.has("instanceId") && this.instanceId) void this.load();
  }

  private async load() {
    this.loading = true;
    try {
      const [draft, instance, history] = await Promise.all([
        getDraft(this.instanceId),
        getInstance(this.instanceId).catch(() => null),
        listGenerations(this.instanceId).catch(() => ({ items: [] as Generation[], next: null })),
      ]);
      this.draft = draft;
      this.instance = instance;
      this.live = history.items.find((g) => g.state === "active") ?? null;
      this.lastFailed = history.items.find((g) => g.state === "publish_failed") ?? null;
      this.failure = null;
    } catch (err) {
      this.failure = parseError(err);
      this.draft = null;
    } finally {
      this.loading = false;
    }
  }

  /* ── derived facts the dialog turns on ────────────────────────────── */

  /** Whether this publish replaces the running task. It is the SERVER's answer,
   * read off the verdict — never re-derived from a class string, because a client
   * that guessed could disagree with the refusal it is about to receive. */
  private get restarts(): boolean {
    return this.draft?.verdict?.restarts ?? false;
  }

  /** The authors whose work is not the caller's own. The rule is about the LAST
   * toucher, because the question the acknowledgement answers is "am I about to
   * ship something I have not read", and the current state of an object is what
   * would ship. An unsigned change counts as somebody else's. */
  private get others(): DraftAuthor[] {
    return (this.draft?.authors ?? []).filter(
      (a) => a.account === "" || a.account !== this.accountId,
    );
  }

  private get blocking(): GateFinding[] {
    return (this.draft?.findings ?? []).filter(
      (f) => f.severity === "error" || f.severity === "blocking-warning",
    );
  }

  private get overridable(): boolean {
    const blocking = this.blocking;
    return blocking.length > 0 && blocking.every((f) => f.overridable);
  }

  /* ── publish ──────────────────────────────────────────────────────── */

  private openConfirm() {
    const draft = this.draft;
    if (!draft) return;
    this.confirming = true;
    this.publishFailure = null;
    this.acceptRestart = false;
    this.acknowledgeAuthors = false;
    this.overrideReason = "";
    this.confirmName = "";
    this.lockoutBlocked = false;
  }

  private closeConfirm() {
    this.confirming = false;
    this.publishing = false;
  }

  private async confirmPublish() {
    const draft = this.draft;
    if (!draft) return;
    this.publishing = true;
    this.publishFailure = null;
    try {
      const started = await publish(this.instanceId, {
        message: this.message.trim() || undefined,
        // Always echoed. It is optimistic concurrency over content nobody else may
        // have changed under us, and it is required outright on the retry after a
        // failed publish.
        expected_digest: draft.rendered_digest,
        allow_restart: this.restarts ? this.acceptRestart : undefined,
        acknowledge_authors: this.others.length > 0 ? this.acknowledgeAuthors : undefined,
        override_reason: this.overrideReason.trim() || undefined,
        confirm_instance_name: this.confirmName.trim() || undefined,
      });
      this.confirming = false;
      this.publishing = false;
      this.job = started.job;
      this.result = null;
      await this.watch(started.job.id);
    } catch (err) {
      const failure = parseError(err);
      this.publishFailure = failure;
      this.publishing = false;
      if (errorDetail<string>(failure, "reason") === "lockout_confirmation_required") {
        this.lockoutBlocked = true;
      }
    }
  }

  private async watch(jobId: string) {
    this.watchAbort?.abort();
    this.watchAbort = new AbortController();
    this.watchLost = false;
    try {
      const done = await watchJob<PublishResult>(jobId, {
        onUpdate: (job) => (this.job = job),
        signal: this.watchAbort.signal,
      });
      const failed = jobFailure(done);
      if (failed) {
        toastPersistent(failed.message, "error", "Publish failed");
      } else if (done.result) {
        this.result = done.result;
        toastPersistent(
          `Generation ${done.result.generation} is live. The instance read back the digest we rendered, which is what makes this true.`,
          "success",
          "Publish confirmed",
        );
      }
      await this.load();
    } catch {
      this.watchLost = true;
      await this.load();
    }
  }

  /* ── discard ──────────────────────────────────────────────────────── */

  private async confirmDiscard() {
    const draft = this.draft;
    if (!draft) return;
    this.discardBusy = true;
    this.discardFailure = null;
    try {
      await discardDraft(this.instanceId, {
        expected_digest: draft.rendered_digest,
        discard_others: this.others.length > 0 ? this.discardOthers : undefined,
      });
      this.discarding = false;
      await this.load();
    } catch (err) {
      this.discardFailure = parseError(err);
    } finally {
      this.discardBusy = false;
    }
  }

  /* ── rendering ────────────────────────────────────────────────────── */

  private supportRows(err: ApiError): BnKvRow[] {
    const rows: BnKvRow[] = [{ label: "Error code", value: err.code }];
    if (err.requestId) rows.push({ label: "Request id", value: err.requestId });
    return rows;
  }

  /**
   * The blast-radius block. There are two of them and they are deliberately not
   * one component with a colour prop: the reload statement and the restart
   * statement have different words, a different tone and a different shape, because
   * two confirmations that look alike train a reader to click through the one that
   * costs two orders of magnitude more.
   */
  private renderRadius(draft: Draft): TemplateResult {
    if (!this.restarts) {
      return html`<div class="radius reload">
        <div class="head">
          <bn-badge .variant=${publishClassVariant(draft.publish_path)}>no restart</bn-badge>
          <span>${CHANGE_CLASS_LABELS[draft.change_class] ?? draft.change_class}</span>
        </div>
        <p><strong>${draft.blast_radius}</strong></p>
        ${draft.verdict?.reason ? html`<p>${draft.verdict.reason}</p>` : nothing}
      </div>`;
    }
    return html`<div class="radius restart">
      <div class="head">
        <bn-badge .variant=${publishClassVariant(draft.publish_path)}>restart</bn-badge>
        <span>${CHANGE_CLASS_LABELS[draft.change_class] ?? draft.change_class}</span>
      </div>
      <p><strong>${draft.blast_radius}</strong></p>
      ${draft.query_loss_warning ? html`<p>${draft.query_loss_warning}</p>` : nothing}
      <p>
        Zero query loss on a restart-class publish is <strong>not achievable today</strong>: Sluice
        stops draining its REST listener after a fixed 10 seconds and its shutdown timeout is not
        configurable, so a query still running at that point is dropped. This instance runs one
        replica — that is an invariant of the product, not a setting — so there is no second task to
        serve queries during the gap.
      </p>
      ${draft.verdict?.reason ? html`<p>${draft.verdict.reason}</p>` : nothing}
      ${draft.verdict && draft.verdict.causes.length > 0
        ? html`<p>These changes are what make it a restart:</p>
            <ul class="plain">
              ${draft.verdict.causes.map((c) => html`<li>${c.detail} <span class="meta">(${c.trigger})</span></li>`)}
            </ul>`
        : nothing}
      ${draft.verdict?.unclassified?.length
        ? html`<p>
              <strong>The classifier could not place these objects</strong>, so it failed closed and
              called the publish restart-class. That is the safe direction and it is not a guess
              being hidden: an object the table does not know might need the process replaced, and
              assuming otherwise is how a hot reload silently fails to take effect.
            </p>
            <ul class="plain">
              ${draft.verdict.unclassified.map((o) => html`<li>${o}</li>`)}
            </ul>`
        : nothing}
    </div>`;
  }

  /** The gate ladder (`SC-F709`). `not-run` is rendered as itself, with the reason
   * it did not run: this screen must not start three containers on the pinned image
   * on every page load, and the publish route runs the full ladder server-side. */
  private renderGates(draft: Draft) {
    const proxied = new Set(["config_validate", "policy_validate", "tests"]);
    return html`<bn-card heading="Pre-publish gate">
      <p class="copy">
        The gate runs <strong>server-side</strong> on every publish, whoever asks — a browser and a
        scoped token meet the identical ladder. What you see here is this screen's read of it.
      </p>
      <ul class="gates">
        ${draft.gates.map(
          (step: GateStep) => html`<li>
            <bn-badge .variant=${STEP_TONE[step.status] ?? "neutral"}>
              ${STEP_WORD[step.status] ?? step.status}
            </bn-badge>
            <span>${GATE_STEP_LABELS[step.step] ?? step.step}</span>
            <span class="detail">
              ${step.detail
                ? step.detail
                : step.status === "not-run" && proxied.has(String(step.step))
                  ? "Not run on this screen: it starts a one-shot container on your instance's pinned image. The publish itself runs it."
                  : ""}
            </span>
          </li>`,
        )}
      </ul>
      ${draft.disagreement
        ? html`<p class="copy" style="margin-top: var(--bino-space-md)">
            <strong>The control plane blocked something the pinned binary accepted:</strong>
            ${draft.disagreement}
          </p>`
        : nothing}
      ${draft.require_tests
        ? html`<p class="meta" style="margin-top: var(--bino-space-sm)">
            This organization requires the stored test suites to pass before a publish.
          </p>`
        : nothing}
    </bn-card>`;
  }

  /** The findings, grouped by object. One shape for every rung's findings is what
   * makes the grouping possible at all. */
  private renderFindings(draft: Draft) {
    if (draft.findings.length === 0) return nothing;
    const groups = new Map<string, GateFinding[]>();
    for (const f of draft.findings) {
      const key = f.kind && f.name ? `${f.kind}/${f.name}` : "The bundle as a whole";
      const list = groups.get(key) ?? [];
      list.push(f);
      groups.set(key, list);
    }
    return html`<bn-card heading=${`${draft.findings.length} finding(s)`}>
      ${[...groups.entries()].map(
        ([object, findings]) => html`<div style="margin-bottom: var(--bino-space-md)">
          <h2>${object}</h2>
          <ul class="findings">
            ${findings.map(
              (f) => html`<li>
                <bn-badge .variant=${FINDING_TONE[f.severity] ?? "neutral"}>${f.severity}</bn-badge>
                ${f.rule ? html`<span class="where">${f.rule}</span>` : nothing}
                <span class="where">${GATE_STEP_LABELS[f.step] ?? f.step}</span>
                ${f.path ? html`<span class="where">${f.path}</span>` : nothing}
                ${f.overridable ? html`<bn-badge variant="neutral">overridable</bn-badge>` : nothing}
                <p class="msg">${f.message}</p>
              </li>`,
            )}
          </ul>
        </div>`,
      )}
      ${this.blocking.length > 0
        ? html`<p class="copy">
            ${this.blocking.length} of these block the publish.
            ${this.overridable
              ? "An owner may proceed past them with a written reason, which is recorded on the generation."
              : "They cannot be overridden: publishing a bundle the pinned binary refuses guarantees a failed publish rather than risking one."}
          </p>`
        : nothing}
    </bn-card>`;
  }

  /** The changed objects with their attribution (`06` §3.9.1). The draft is shared —
   * one per instance, not one per person — so this list is how a publisher learns
   * whose work they are about to ship. */
  private renderChanges(draft: Draft) {
    const rows: BnRow[] = draft.changes.map((c) => ({
      op: html`<span class="op ${c.op}">${c.op === "create" ? "+" : c.op === "delete" ? "−" : "~"}</span>`,
      object: html`<span>${c.kind}/${c.name}</span>`,
      who: html`<span>${c.changed_by_label || c.changed_by || "system"}</span>
        <span class="sub">${when(c.changed_at)}</span>`,
    }));
    return html`<bn-card flush heading=${`${draft.changes.length} change(s) in this draft`}>
      <bn-table caption="Changed objects" .columns=${CHANGE_COLUMNS} .rows=${rows}></bn-table>
    </bn-card>`;
  }

  private renderAuthors(draft: Draft) {
    if (draft.authors.length <= 1 && this.others.length === 0) return nothing;
    return html`<bn-card heading="Who wrote what is in this draft">
      <p class="copy">
        There is one draft per instance, not one per person, so publishing ships everything anybody
        has saved. Comments, locks and presence indicators are deliberately not part of this: the
        acknowledgement below is a speed bump against publishing work you have not read.
      </p>
      <ul class="authors">
        ${draft.authors.map(
          (a) => html`<li>
            <strong>${a.label || a.account || "system"}</strong>
            <span class="meta">${a.object_count} object(s), last edited ${when(a.last_edit_at)}</span>
            ${a.account !== "" && a.account === this.accountId
              ? html`<bn-badge variant="neutral">you</bn-badge>`
              : nothing}
          </li>`,
        )}
      </ul>
      ${draft.oldest_change_at
        ? html`<p class="meta" style="margin-top: var(--bino-space-sm)">
            The oldest unpublished change in this draft was made ${when(draft.oldest_change_at)}.
          </p>`
        : nothing}
    </bn-card>`;
  }

  /**
   * The publish confirmation. The two classes are two dialogs: different heading,
   * different tone, different acknowledgements and a different button — because
   * `SC-S404` requires that they "look different, not merely say different
   * numbers".
   */
  private renderConfirm(draft: Draft) {
    if (!this.confirming) return nothing;
    const restarts = this.restarts;
    const others = this.others;
    const needsAuthors = others.length > 0;
    const needsOverride = this.blocking.length > 0 && this.overridable;
    const ready =
      (!restarts || this.acceptRestart) &&
      (!needsAuthors || this.acknowledgeAuthors) &&
      (!needsOverride || this.overrideReason.trim() !== "") &&
      (!this.lockoutBlocked || (this.confirmName.trim() !== "" && this.overrideReason.trim() !== ""));
    return html`<bn-modal
      open
      .width=${660}
      tone=${restarts ? "destructive" : "default"}
      heading=${restarts
        ? `Publish generation ${draft.generation} and restart the instance?`
        : `Publish generation ${draft.generation}?`}
      @close=${() => this.closeConfirm()}
    >
      <p class="copy">
        ${draft.changes.length} change(s), rendering to
        <code>${short(draft.rendered_digest)}</code> (${draft.rendered_bytes} bytes).
      </p>
      ${needsAuthors
        ? html`<p class="copy">
              <strong>${others.reduce((n, a) => n + a.object_count, 0)} of them are not yours:</strong>
            </p>
            <ul class="plain">
              ${others.map(
                (a) => html`<li>
                  ${a.label || "an unsigned change"} — ${a.object_count} object(s), last edited
                  ${when(a.last_edit_at)}
                </li>`,
              )}
            </ul>`
        : nothing}
      <bn-field
        label="Message"
        hint="The only human record of why this generation exists. It appears in the history next to your name."
      >
        <bn-input
          .value=${this.message}
          @input=${(e: CustomEvent<{ value: string }>) => (this.message = e.detail.value)}
        ></bn-input>
      </bn-field>
      ${needsOverride
        ? html`<div style="margin-top: var(--bino-space-md)">
            <bn-field
              label="Why are you publishing past ${this.blocking.length} blocking finding(s)?"
              required
              hint="Recorded on the generation. An empty reason is refused at the route rather than defaulted, because an empty value is the statement that no override was exercised."
            >
              <bn-input
                .value=${this.overrideReason}
                @input=${(e: CustomEvent<{ value: string }>) => (this.overrideReason = e.detail.value)}
              ></bn-input>
            </bn-field>
          </div>`
        : nothing}
      ${this.lockoutBlocked
        ? html`<div style="margin-top: var(--bino-space-md)">
            <p class="copy">
              <strong>This draft leaves the instance with no allow rule.</strong> Every query will be
              denied with <code>ACL_DENIED</code> and the message "no SqlAccessPolicy matched
              (default-deny)". That is a safe failure — it is not a safe surprise.
            </p>
            <bn-field label="Type the instance name to confirm" required>
              <bn-input
                mono
                .value=${this.confirmName}
                placeholder=${this.instance?.name ?? ""}
                @input=${(e: CustomEvent<{ value: string }>) => (this.confirmName = e.detail.value)}
              ></bn-input>
            </bn-field>
          </div>`
        : nothing}
      ${this.publishFailure
        ? html`<div role="alert" style="margin-top: var(--bino-space-md)">
            <p class="copy">${this.publishFailure.message}</p>
            <bn-kv .rows=${this.supportRows(this.publishFailure)}></bn-kv>
          </div>`
        : nothing}

      <div slot="consequence">
        ${restarts
          ? html`<p style="margin: 0 0 var(--bino-space-sm)">${draft.blast_radius}</p>
              ${draft.query_loss_warning
                ? html`<p style="margin: 0 0 var(--bino-space-sm)">${draft.query_loss_warning}</p>`
                : nothing}
              <p style="margin: 0 0 var(--bino-space-sm)">
                Zero query loss is not achievable on a restart: the drain is a fixed 10 seconds and is
                not configurable, and this instance runs one replica.
              </p>
              <bn-checkbox
                label="I accept the interruption this publish causes"
                hint="This is a different decision from saving a draft, and it is asked separately for that reason."
                .checked=${this.acceptRestart}
                @change=${(e: CustomEvent<{ checked: boolean }>) =>
                  (this.acceptRestart = e.detail.checked)}
              ></bn-checkbox>`
          : html`<p style="margin: 0 0 var(--bino-space-sm)">${draft.blast_radius}</p>
              <p style="margin: 0">
                Nothing restarts. The bundle is written into the instance's volume and re-read in
                place, so there is no interruption to accept.
              </p>`}
        ${needsAuthors
          ? html`<div style="margin-top: var(--bino-space-sm)">
              <bn-checkbox
                label="I have reviewed the changes made by other people in this draft"
                .checked=${this.acknowledgeAuthors}
                @change=${(e: CustomEvent<{ checked: boolean }>) =>
                  (this.acknowledgeAuthors = e.detail.checked)}
              ></bn-checkbox>
            </div>`
          : nothing}
      </div>

      <bn-button slot="footer" @click=${() => this.closeConfirm()}>Cancel</bn-button>
      <bn-button
        slot="footer"
        variant=${restarts ? "danger" : "primary"}
        ?disabled=${!ready}
        ?loading=${this.publishing}
        @click=${() => void this.confirmPublish()}
      >
        ${restarts ? "Publish and restart" : "Publish — no restart"}
      </bn-button>
    </bn-modal>`;
  }

  /** The discard dialog. Destructive and irreversible: the draft is not versioned,
   * so discarded work is gone — and discarding somebody else's work needs a second,
   * separate acknowledgement. */
  private renderDiscard(draft: Draft) {
    if (!this.discarding) return nothing;
    const others = this.others;
    const ready = others.length === 0 || this.discardOthers;
    return html`<bn-modal
      open
      tone="destructive"
      .width=${600}
      heading="Discard every unpublished change?"
      @close=${() => (this.discarding = false)}
    >
      <p class="copy">
        This reverts the draft to
        ${this.live ? `generation ${this.live.generation}` : "the empty starting configuration"}.
        The draft is not versioned, so this cannot be undone and the work is gone.
      </p>
      ${others.length > 0
        ? html`<p class="copy"><strong>Some of it is not yours:</strong></p>
            <ul class="plain">
              ${others.map(
                (a) => html`<li>${a.label || "an unsigned change"} — ${a.object_count} object(s)</li>`,
              )}
            </ul>`
        : nothing}
      ${this.discardFailure
        ? html`<div role="alert" style="margin-top: var(--bino-space-md)">
            <p class="copy">${this.discardFailure.message}</p>
            <bn-kv .rows=${this.supportRows(this.discardFailure)}></bn-kv>
          </div>`
        : nothing}
      ${others.length > 0
        ? html`<div slot="consequence">
            <bn-checkbox
              label="I am discarding other people's unpublished work as well"
              .checked=${this.discardOthers}
              @change=${(e: CustomEvent<{ checked: boolean }>) =>
                (this.discardOthers = e.detail.checked)}
            ></bn-checkbox>
          </div>`
        : nothing}
      <bn-button slot="footer" @click=${() => (this.discarding = false)}>Keep the draft</bn-button>
      <bn-button
        slot="footer"
        variant="danger"
        ?disabled=${!ready}
        ?loading=${this.discardBusy}
        @click=${() => void this.confirmDiscard()}
      >
        Discard ${draft.changes.length} change(s)
      </bn-button>
    </bn-modal>`;
  }

  /**
   * The publish in flight, and the confirmation as the distinct step it is
   * (`SC-F608`). The last step is the digest read-back, and until it comes back
   * equal this screen says "not confirmed" rather than "published".
   */
  private renderJob() {
    const job = this.job;
    if (!job) return nothing;
    const result = this.result;
    const failed = isTerminal(job.status) ? jobFailure(job) : null;
    if (result) {
      const reload = result.path === "hot_reload";
      return html`<bn-card heading=${`Generation ${result.generation} is live`}>
        <p class="copy">
          <strong>Confirmed by the instance.</strong> The control plane read
          <code>config_digest</code> back from the instance's own <code>/admin/healthz</code> and it
          equals the digest it rendered. That equality is what makes this generation live — nothing
          else does.
        </p>
        <bn-kv
          .rows=${[
            { label: "Rendered digest", value: result.rendered_digest },
            { label: "Digest the instance reported", value: result.observed_digest },
            { label: "Path", value: reload ? "hot reload — no task restart" : "rolling restart" },
            { label: "Reload triggered", value: when(result.reload.triggered) },
            { label: "Digest confirmed", value: when(result.reload.confirmed) },
            {
              label: "Measured unavailability",
              value:
                result.downtime_ms === null
                  ? undefined
                  : result.downtime_ms === 0
                    ? "none — no task was replaced"
                    : `${result.downtime_ms} ms`,
            },
          ] as BnKvRow[]}
          unsetText="not measured"
        ></bn-kv>
        ${result.unhealthy_after && result.unhealthy_after.length > 0
          ? html`<p class="copy" style="margin-top: var(--bino-space-sm)">
              Catalogs still unhealthy after this publish: ${result.unhealthy_after.join(", ")}.
              ${result.unhealthy_before && result.unhealthy_before.length > 0
                ? html`They were already unhealthy before it: ${result.unhealthy_before.join(", ")}.`
                : nothing}
            </p>`
          : nothing}
        <div style="margin-top: var(--bino-space-md)">
          <bn-button
            @click=${() =>
              navigate(hrefFor("instances", this.instanceId, "generations", result.generation))}
          >
            Generation ${result.generation}
          </bn-button>
        </div>
      </bn-card>`;
    }
    if (failed) {
      return html`<bn-card heading="The publish failed">
        <div class="failure" role="alert">
          <p>
            <strong>
              ${this.live
                ? `Generation ${this.live.generation} is still live and still serving queries.`
                : "Nothing became live."}
            </strong>
            A publish is only successful when the instance confirms the digest; anything else — a
            timeout, a mismatch, an error envelope, a writer job that exited non-zero — leaves the new
            generation <code>publish_failed</code> and the previous one running.
          </p>
          <p>${failed.message}</p>
          <bn-kv .rows=${this.supportRows(failed)}></bn-kv>
          <div class="actions">
            <bn-button variant="primary" @click=${() => void this.load()}>Reload the draft</bn-button>
            <bn-button @click=${() => navigate(hrefFor("instances", this.instanceId, "generations"))}>
              Publish history
            </bn-button>
          </div>
        </div>
      </bn-card>`;
    }
    return html`<bn-card heading="Publishing">
      <ul class="steps">
        <li>
          <bn-spinner size="16"></bn-spinner>
          <span>
            ${job.progress
              ? `${job.progress.step} — step ${job.progress.index} of ${job.progress.total}`
              : `Job ${job.status}`}
          </span>
        </li>
      </ul>
      <p class="copy">
        The last step is the one that decides the outcome: the control plane reads the digest back
        from the instance and compares it with what it rendered. Until then this publish is in
        flight, not done.
      </p>
      ${this.watchLost
        ? html`<p class="copy">
            <strong>This page lost contact with the job, not the other way round.</strong> The publish
            is still running server-side; the generation history is the authority on how it ended.
          </p>`
        : nothing}
    </bn-card>`;
  }

  /** The clean state: nothing to publish. It is not an error and not a 404 — an
   * instance whose draft was just published has none — and it names what is
   * running so the screen still answers the question the reader came with. */
  private renderClean(draft: Draft) {
    return html`<bn-empty-state
      icon="✓"
      heading="Nothing to publish"
      message=${this.live
        ? `Generation ${this.live.generation} is live, and the draft renders byte for byte identically to it — the same manifests, the same server configuration, the same secrets and the same image. Publishing it again would be refused, because publishing twice must not cost an outage.`
        : "This instance has never published, and its draft is empty. An empty policy directory is valid in Sluice and means deny everything: connect a datasource and author a policy, then publish."}
    >
      <bn-button
        slot="actions"
        @click=${() => navigate(hrefFor("instances", this.instanceId, "policies"))}
      >
        Policies
      </bn-button>
      <bn-button
        slot="actions"
        @click=${() => navigate(hrefFor("instances", this.instanceId, "datasources"))}
      >
        Datasources
      </bn-button>
      ${draft.based_on_generation !== null
        ? html`<bn-button
            slot="actions"
            @click=${() => navigate(hrefFor("instances", this.instanceId, "generations"))}
          >
            Publish history
          </bn-button>`
        : nothing}
    </bn-empty-state>`;
  }

  override render() {
    if (this.loading && !this.draft) {
      return html`<bn-spinner block size="28" label="Reading the draft"></bn-spinner>`;
    }
    if (this.failure) {
      return html`<a class="back" href=${hrefFor("instances", this.instanceId)}>← Instance</a>
        <bn-card heading="Could not read the draft">
          <div class="failure" role="alert">
            <p>${this.failure.message}</p>
            <bn-kv .rows=${this.supportRows(this.failure)}></bn-kv>
            ${this.failure.requestId
              ? nothing
              : html`<p>
                  This request did not reach Sluice Cloud, so there is no reference to quote.
                </p>`}
            <div class="actions">
              <bn-button variant="primary" @click=${() => void this.load()}>Try again</bn-button>
            </div>
          </div>
        </bn-card>`;
    }
    const draft = this.draft;
    if (!draft) return nothing;
    const blocked = this.blocking.length > 0 && !this.overridable;
    return html`
      <a class="back" href=${hrefFor("instances", this.instanceId)}>← Instance</a>
      <header>
        <div class="head-text">
          <h1>Publish</h1>
          <p class="lede">
            Editing is never live. This screen is the whole of what a publish would ship — the draft
            IS generation ${draft.generation}, in state draft — together with what it costs and who
            wrote it.
            ${this.live
              ? html`Generation ${this.live.generation} is running right now.`
              : html`Nothing has been published on this instance yet.`}
          </p>
        </div>
        <div class="head-actions">
          <bn-button
            @click=${() =>
              navigate(`${hrefFor("instances", this.instanceId, "diff")}?from=live&to=draft`)}
          >
            See the diff
          </bn-button>
          ${draft.dirty
            ? html`<bn-button variant="danger" @click=${() => (this.discarding = true)}>
                  Discard draft
                </bn-button>
                <bn-button
                  variant="primary"
                  ?disabled=${blocked}
                  @click=${() => this.openConfirm()}
                >
                  Publish…
                </bn-button>`
            : nothing}
        </div>
      </header>

      <div class="stack">
        ${this.renderJob()}
        ${draft.retry_requires_expected_digest
          ? html`<bn-panel eyebrow="The previous publish failed">
              <p class="copy">
                ${this.lastFailed
                  ? html`Generation ${this.lastFailed.generation} failed to publish.`
                  : html`A previous publish on this instance failed.`}
                The instance is not locked — you may retry immediately — but the retry must carry the
                digest of the draft as it stands now, which this screen sends for you. The point of the
                rule is that a client cannot re-submit content nobody has looked at since the failure.
              </p>
              ${this.lastFailed?.failure_reason
                ? html`<p class="copy">${this.lastFailed.failure_reason}</p>`
                : nothing}
            </bn-panel>`
          : nothing}
        ${draft.dirty
          ? html`
              ${this.renderRadius(draft)} ${this.renderChanges(draft)} ${this.renderAuthors(draft)}
              ${this.renderGates(draft)} ${this.renderFindings(draft)}
              <bn-card heading="What would ship">
                <bn-kv
                  .rows=${[
                    { label: "Becomes generation", value: String(draft.generation) },
                    {
                      label: "Based on",
                      value:
                        draft.based_on_generation === null
                          ? "nothing — the first publish"
                          : `generation ${draft.based_on_generation}`,
                    },
                    { label: "Rendered digest", value: draft.rendered_digest },
                    { label: "Rendered size", value: `${draft.rendered_bytes} bytes` },
                  ] as BnKvRow[]}
                ></bn-kv>
                <p class="meta" style="margin-top: var(--bino-space-sm)">
                  The digest is computed over the bytes the instance will read. It is what the publish
                  confirmation compares against, and it is what this screen sends as
                  <code>expected_digest</code> so a colleague's save between reading and publishing is
                  refused rather than shipped unseen.
                </p>
              </bn-card>
            `
          : this.renderClean(draft)}
      </div>
      ${this.renderConfirm(draft)} ${this.renderDiscard(draft)}
    `;
  }
}

/** short renders a digest the way a reader compares one — by eye, on the first and
 * last characters. */
function short(digest: string): string {
  if (!digest) return "—";
  const bare = digest.replace(/^sha256:/, "");
  return bare.length <= 20 ? bare : `${bare.slice(0, 10)}…${bare.slice(-6)}`;
}

/** when renders a timestamp in the reader's locale, falling back to the raw value
 * rather than to "Invalid Date". */
function when(iso: string): string {
  if (!iso) return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString();
}

declare global {
  interface HTMLElementTagNameMap {
    "page-publish": PagePublish;
  }
}
